import { makeStyles } from '@material-ui/core/styles';
import TextField from '@material-ui/core/TextField';
import { CareerSiteSettings, ChatbotWidgetBrand } from 'api-clients/monolith';
import React, { VFC } from 'react';
import { useIntl } from 'react-intl';
import { useSelector } from 'react-redux';

import {
  makeIsCareerSiteInterdependentFiltersEnabled,
  makeIsCareerSiteTranslationEnabled,
} from 'containers/Auth_old/selectors';
import { Description } from 'containers/Chatbot/ChatbotWidget/Components/Description';
import { SectionTitle } from 'containers/Chatbot/ChatbotWidget/Components/SectionTitle';
import { SettingTitle } from 'containers/Chatbot/ChatbotWidget/Components/SettingTitle';
import { BrandSelector } from 'containers/Chatbot/ChatbotWidget/GeneralSettings/BrandSelector';

import { DefaultLocationSelector } from './Components/DefaultLocationSelector';
import { LanguageSelector } from './Components/LanguageSelector';
import { SettingSwitch } from './Components/SettingSwitch';
import { messages } from './messages';
import { HandleSettingChange } from './types';

const useStyles = makeStyles(theme => ({
  title: {
    marginTop: theme.spacing(2),
  },
  switch: {
    marginTop: theme.spacing(4),
  },
  primaryTitle: {
    marginTop: theme.spacing(4),
  },
  categoryInput: {
    margin: theme.spacing(2, 0),
  },
  block: {
    display: 'block',
  },
  hidden: {
    display: 'none',
  },
}));

export interface GeneralSettingsProps {
  brands: ChatbotWidgetBrand[];
  selectedBrandOption: ChatbotWidgetBrand;
  handleBrandChange: (brands: ChatbotWidgetBrand) => void;
  settings: CareerSiteSettings;
  handleSettingChange: HandleSettingChange;
}

export const GeneralSettings: VFC<GeneralSettingsProps> = ({
  brands,
  selectedBrandOption,
  handleBrandChange,
  settings,
  handleSettingChange,
}) => {
  const styles = useStyles();
  const intl = useIntl();
  const careerSiteTranslationEnabled = useSelector(
    makeIsCareerSiteTranslationEnabled(),
  );
  const careerSiteInterdependentFiltersEnabled = useSelector(
    makeIsCareerSiteInterdependentFiltersEnabled(),
  );

  return (
    <section>
      <SectionTitle value={messages.generalSettings} />
      <SettingTitle value={messages.brandYourWidget} className={styles.title} />
      <Description value={messages.brandYourWidgetDesc} />
      <BrandSelector
        brands={brands}
        selectedBrand={selectedBrandOption}
        onChange={handleBrandChange}
      />
      <Description value={messages.additionaBrandInfo} />

      {careerSiteInterdependentFiltersEnabled && (
        <>
          <SettingSwitch
            settingKey="display_location_address"
            label={messages.displayLocationAddressSwitchLabel}
            checked={!!settings.display_location_address}
            onChange={handleSettingChange}
          />
          <Description value={messages.displayLocationAddressSwitchDesc} />
        </>
      )}

      <SettingTitle
        value={messages.jobsDirectoryFilters}
        className={styles.primaryTitle}
      />

      {careerSiteTranslationEnabled && (
        <>
          <SettingSwitch
            settingKey="jobs_directory_translations"
            label={messages.jobsDirectoryTranslationsTitle}
            checked={settings.jobs_directory_translations}
            onChange={handleSettingChange}
          />
          <Description value={messages.jobsDirectoryTranslationsDescription} />

          {settings.jobs_directory_translations && (
            <LanguageSelector
              settingKey="default_locale"
              title={messages.defaultLanguageTitle}
              description={messages.defaultLanguageDescription}
              placeholder={messages.selectDefaultLanguage}
              selectedLanguage={settings.default_locale}
              onChange={handleSettingChange}
            />
          )}
        </>
      )}

      {!careerSiteInterdependentFiltersEnabled && (
        <>
          <SettingSwitch
            settingKey="display_compensation_type"
            label={messages.displayCompensationTypeSwitchLabel}
            checked={settings.display_compensation_type}
            onChange={handleSettingChange}
          />
          <Description value={messages.displayCompensationTypeSwitchDesc} />
        </>
      )}

      {careerSiteInterdependentFiltersEnabled ? (
        <>
          <SettingSwitch
            settingKey="display_location_filter"
            label={messages.displayLocationNameSwitchLabel}
            checked={settings.display_location_filter}
            onChange={handleSettingChange}
          />
          <Description value={messages.displayLocationNameSwitchDesc} />
        </>
      ) : (
        <>
          <SettingSwitch
            settingKey="display_location_filter"
            label={messages.displayLocationFilterSwitchLabel}
            checked={settings.display_location_filter}
            onChange={handleSettingChange}
          />
          <Description value={messages.displayLocationFilterSwitchDesc} />

          <SettingSwitch
            settingKey="display_radius_filter"
            label={messages.displayRadiusFilterSwitchLabel}
            checked={settings.display_radius_filter}
            onChange={handleSettingChange}
          />
          <Description value={messages.displayRadiusFilterSwitchDesc} />

          <DefaultLocationSelector
            settingKey="default_location_filter"
            title={messages.defaultLocationFilterTitle}
            description={messages.defaultLocationFilterDescription}
            placeholder={messages.selectDefaultLocationFilter}
            selectedLocation={settings.default_location_filter}
            onChange={handleSettingChange}
          />
        </>
      )}

      <SettingSwitch
        settingKey="display_position_filter"
        label={messages.displayPositionFilterSwitchLabel}
        checked={settings.display_position_filter}
        onChange={handleSettingChange}
      />
      <Description value={messages.displayPositionFilterSwitchDesc} />

      <SettingSwitch
        settingKey="display_category_filter"
        label={messages.displayCategoryFilterSwitchLabel}
        checked={settings.display_category_filter}
        onChange={handleSettingChange}
      />
      <Description value={messages.displayCategoryFilterSwitchDesc} />

      <div
        className={
          settings.display_category_filter ? styles.block : styles.hidden
        }
      >
        <SettingTitle
          value={messages.categoryFilterLabel}
          className={styles.primaryTitle}
        />
        <TextField
          id="category_filter_label"
          className={styles.categoryInput}
          fullWidth
          value={
            settings?.category_filter_label ??
            intl.formatMessage(messages.categoryFilterLabelDefault)
          }
          onChange={e =>
            handleSettingChange('category_filter_label', e.target.value)
          }
          margin="normal"
          InputLabelProps={{
            shrink: true,
          }}
          variant="outlined"
          disabled={!settings?.display_category_filter}
        />
      </div>

      <SettingSwitch
        settingKey="display_job_type_filter"
        label={messages.displayJobTypeFilterSwitchLabel}
        checked={settings.display_job_type_filter}
        onChange={handleSettingChange}
      />
      <Description value={messages.displayJobTypeFilterSwitchDesc} />

      <SettingTitle
        value={messages.aiJobCategorization}
        className={styles.primaryTitle}
      />
      <SettingSwitch
        settingKey="categorize_jobs_by_job_type"
        label={messages.categorizationSwitchLabel}
        checked={settings.categorize_jobs_by_job_type}
        onChange={handleSettingChange}
      />
      <Description value={messages.categorizationSwitchDesc} />

      {!careerSiteInterdependentFiltersEnabled && (
        <>
          <SettingSwitch
            settingKey="categorize_jobs_by_experience_level"
            label={messages.experienceLevelSwitchLabel}
            checked={settings.categorize_jobs_by_experience_level}
            onChange={handleSettingChange}
            className={styles.title}
          />
          <Description value={messages.experienceLevelSwitchDesc} />
        </>
      )}
    </section>
  );
};
